import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, ChevronLeft } from 'lucide-react';

interface DepartmentReportProps {
    onMenuSelect: (menuId: string, params?: any) => void;
    tenantName: string;
    apiFetch: (endpoint: string, options?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
    selectedYear: number;
}

interface Department {
    id: number;
    name: string;
}

interface RowData {
    daikubun_id: number;
    daikubun: string;
    total: number;
    departments: Record<string, number>;
}

interface CategoryData {
    category_id: number;
    label: string;
    is_revenue: boolean;
    total: number;
    departments_total: Record<string, number>;
    rows: RowData[];
}

interface SummaryData {
    total: number;
    departments: Record<string, number>;
}

interface ReportData {
    fiscal_year: number;
    departments: Department[];
    categories: CategoryData[];
    summary: Record<string, SummaryData>;
}

const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return "¥0";
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(amount);
};

const DepartmentReport: React.FC<DepartmentReportProps> = ({
    onMenuSelect,
    tenantName,
    apiFetch,
    currentTenantId,
    selectedYear
}) => {
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ReportData | null>(null);

    const fetchData = async () => {
        if (!currentTenantId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/analysis/department-activity?fiscal_year=${selectedYear}`);
            if (!res.ok) {
                throw new Error('部門別収支データの取得に失敗しました');
            }
            const json = await res.json();
            setData(json);
        } catch (err: any) {
            setError(err.message || 'データ取得エラー');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!currentTenantId) return;
        setDownloading(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/reports/department-activity/excel?fiscal_year=${selectedYear}`);
            if (!res.ok) {
                throw new Error('Excelの生成に失敗しました');
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `department_activity_${selectedYear}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err: any) {
            setError(err.message || 'ダウンロードエラー');
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentTenantId, selectedYear]);

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <button
                        onClick={() => onMenuSelect('main-menu')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#4B5563',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '0',
                            marginBottom: '8px'
                        }}
                    >
                        <ChevronLeft size={16} style={{ marginRight: '4px' }} />
                        メインメニューへ戻る
                    </button>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                        部門別収支報告書
                    </h1>
                    <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>
                        {tenantName} - {selectedYear}年度
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={handleDownloadExcel}
                        disabled={downloading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#10B981',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: downloading ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            opacity: downloading ? 0.7 : 1
                        }}
                    >
                        <Download size={16} style={{ marginRight: '8px' }} />
                        {downloading ? '生成中...' : 'Excel出力'}
                    </button>
                    <button
                        onClick={fetchData}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        <RefreshCw size={16} style={{ marginRight: '8px' }} />
                        更新
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '6px', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <p style={{ color: '#6B7280' }}>データを読み込み中...</p>
                </div>
            ) : data && (
                <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F3F4F6', color: '#4B5563', borderBottom: '1px solid #E5E7EB' }}>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold' }}>区分</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold' }}>勘定科目 / 大区分</th>
                                    {data.departments.map(dept => (
                                        <th key={dept.id} style={{ padding: '12px 16px', fontWeight: 'bold' }}>{dept.name}</th>
                                    ))}
                                    <th style={{ padding: '12px 16px', fontWeight: 'bold', backgroundColor: '#EEF2FF', color: '#4338CA' }}>全体合計</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.categories.map((cat, catIdx) => (
                                    <React.Fragment key={cat.category_id}>
                                        {cat.rows.map((row, rowIdx) => (
                                            <tr key={`${cat.category_id}-${rowIdx}`} style={{ borderBottom: '1px solid #E5E7EB' }}>
                                                {rowIdx === 0 ? (
                                                    <td rowSpan={cat.rows.length} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#111827', verticalAlign: 'top', backgroundColor: '#F9FAFB' }}>
                                                        {cat.label}
                                                    </td>
                                                ) : null}
                                                <td style={{ padding: '8px 16px', textAlign: 'left', color: '#4B5563' }}>{row.daikubun}</td>
                                                {data.departments.map(dept => (
                                                    <td key={dept.id} style={{ padding: '8px 16px' }}>{formatCurrency(row.departments[dept.id.toString()])}</td>
                                                ))}
                                                <td style={{ padding: '8px 16px', fontWeight: '500', backgroundColor: '#EEF2FF', color: '#4B5563' }}>
                                                    {formatCurrency(row.total)}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Category Subtotal */}
                                        <tr style={{ borderBottom: '2px solid #D1D5DB', backgroundColor: '#F3F4F6' }}>
                                            <td colSpan={2} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'bold', color: '#374151' }}>
                                                【{cat.label} 計】
                                            </td>
                                            {data.departments.map(dept => (
                                                <td key={dept.id} style={{ padding: '12px 16px', fontWeight: '600' }}>
                                                    {formatCurrency(cat.departments_total[dept.id.toString()])}
                                                </td>
                                            ))}
                                            <td style={{ padding: '12px 16px', fontWeight: 'bold', backgroundColor: '#E0E7FF', color: '#4338CA' }}>
                                                {formatCurrency(cat.total)}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}

                                {/* Summary Rows */}
                                <tr style={{ borderTop: '2px solid #9CA3AF' }}>
                                    <td colSpan={2} style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold', fontSize: '15px' }}>事業活動収支差額</td>
                                    {data.departments.map(dept => (
                                        <td key={dept.id} style={{ padding: '16px', fontWeight: 'bold', fontSize: '15px' }}>
                                            {formatCurrency(data.summary.service_balance.departments[dept.id.toString()])}
                                        </td>
                                    ))}
                                    <td style={{ padding: '16px', fontWeight: 'bold', fontSize: '15px', backgroundColor: '#EEF2FF' }}>
                                        {formatCurrency(data.summary.service_balance.total)}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold', fontSize: '15px' }}>経常増減差額</td>
                                    {data.departments.map(dept => (
                                        <td key={dept.id} style={{ padding: '16px', fontWeight: 'bold', fontSize: '15px' }}>
                                            {formatCurrency(data.summary.ordinary_balance.departments[dept.id.toString()])}
                                        </td>
                                    ))}
                                    <td style={{ padding: '16px', fontWeight: 'bold', fontSize: '15px', backgroundColor: '#EEF2FF' }}>
                                        {formatCurrency(data.summary.ordinary_balance.total)}
                                    </td>
                                </tr>
                                <tr style={{ borderTop: '2px solid #9CA3AF', backgroundColor: '#F9FAFB' }}>
                                    <td colSpan={2} style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold', fontSize: '16px', color: '#111827' }}>当期活動増減差額</td>
                                    {data.departments.map(dept => (
                                        <td key={dept.id} style={{ padding: '16px', fontWeight: 'bold', fontSize: '16px', color: data.summary.net_change.departments[dept.id.toString()] >= 0 ? '#059669' : '#DC2626' }}>
                                            {formatCurrency(data.summary.net_change.departments[dept.id.toString()])}
                                        </td>
                                    ))}
                                    <td style={{ padding: '16px', fontWeight: 'bold', fontSize: '16px', backgroundColor: '#E0E7FF', color: data.summary.net_change.total >= 0 ? '#059669' : '#DC2626' }}>
                                        {formatCurrency(data.summary.net_change.total)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DepartmentReport;
